import React, { useState } from "react";
import { ColumnDef, SortingState, Updater } from "@tanstack/react-table";
import {
  Performance as PerformanceIcon,
  SplitHorizontal,
  SplitVertical,
  Table as TableIcon
} from "@dreamhub/designsystem/icons";
import {
  DreamerBarChartDataType,
  DreamerLineChartDataType,
  DreamerPieChartDataType,
  DreamerStackBarChartDataType,
  LineConfig,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";
import ChartBody from "@/components/global/Chat/components/dreamerCharts/hooks/components/ChartBody";
import { ChartKind } from "@/components/global/Chat/chartKind";
import DataTable from "@/components/controls/DataTable/DataTable";
import DreamerAttachmentFilterToolbar from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentFilterToolbar";
import DreamerAttachmentModalShell, {
  DreamerModalHeaderSlots
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentModalShell";
import DreamerAttachmentEmptyState, {
  DreamerAttachmentEmptyVariant
} from "@/components/global/Chat/components/dreamerAttachment/DreamerAttachmentEmptyState";
import ShowAsDropdown, {
  ShowAsOption
} from "@/components/global/Chat/ShowAsDropdown";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { Filters, FiltersSetter } from "@/services/api/types";
import { DreamerTableRow } from "@/entities/attachments/types";
import { cn } from "@/utils/utils";

type ModalViewMode = "vertical-split" | "horizontal-split" | "chart" | "table";

const VIEW_MODE_OPTIONS: ShowAsOption<ModalViewMode>[] = [
  {
    mode: "vertical-split",
    icon: <SplitVertical />,
    labelKey: "chat.viewModes.verticalSplit"
  },
  {
    mode: "horizontal-split",
    icon: <SplitHorizontal />,
    labelKey: "chat.viewModes.horizontalSplit"
  },
  {
    mode: "chart",
    icon: <PerformanceIcon />,
    labelKey: "chat.viewModes.chart"
  },
  { mode: "table", icon: <TableIcon />, labelKey: "chat.viewModes.table" }
];

interface ExpandedDreamerChartModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  chartKind?: ChartKind;
  // Only populated for ChartKind.Bar — other kinds use their own data props.
  chartData?: DreamerBarChartDataType[];
  stackedChartData?: DreamerStackBarChartDataType[];
  stackSegments?: StackSegmentConfig[];
  lineChartData?: DreamerLineChartDataType[];
  lineConfigs?: LineConfig[];
  pieChartData?: DreamerPieChartDataType[];
  gaugeValue?: number;
  gaugeTotal?: number;
  gaugeCenterValue?: string;
  gaugeCenterLabel?: string;
  gaugeTooltipLabel?: string;
  isPercentage?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  generatedAt?: string;
  onDownload?: () => void;
  isDownloading?: boolean;
  canDownload?: boolean;
  tableColumns?: ColumnDef<DreamerTableRow, unknown>[];
  tableData?: DreamerTableRow[];
  tableIsLoading?: boolean;
  tableHasMore?: boolean;
  tableIsLoadingMore?: boolean;
  onTableLoadMore?: () => void;
  tableSorting?: SortingState;
  onTableSortingChange?: (updater: Updater<SortingState>) => void;
  filters?: Filters;
  setFilters?: FiltersSetter;
  filterOptions?: DataTableFilter[];
  emptyVariant?: DreamerAttachmentEmptyVariant | null;
  headerSlots?: DreamerModalHeaderSlots;
}

const ExpandedDreamerChartModal: React.FC<ExpandedDreamerChartModalProps> = ({
  isOpen,
  setIsOpen,
  title,
  chartKind = ChartKind.Bar,
  chartData = [],
  stackedChartData = [],
  stackSegments = [],
  lineChartData = [],
  lineConfigs = [],
  pieChartData = [],
  gaugeValue,
  gaugeTotal,
  gaugeCenterValue,
  gaugeCenterLabel,
  gaugeTooltipLabel,
  isPercentage = true,
  xAxisLabel,
  yAxisLabel,
  generatedAt,
  onDownload,
  isDownloading = false,
  canDownload = true,
  tableColumns,
  tableData,
  tableIsLoading = false,
  tableHasMore = false,
  tableIsLoadingMore = false,
  onTableLoadMore,
  tableSorting,
  onTableSortingChange,
  filters,
  setFilters,
  filterOptions = [],
  emptyVariant,
  headerSlots
}) => {
  const [viewMode, setViewMode] = useState<ModalViewMode>("vertical-split");

  const hasTable = !!tableColumns && !!tableData;
  // Fall back to chart-only when no table data is supplied so the dropdown
  // selection can't desync with what the modal can actually render.
  const effectiveViewMode: ModalViewMode = hasTable ? viewMode : "chart";

  const showChart = effectiveViewMode !== "table";
  const showTable = effectiveViewMode !== "chart" && hasTable;
  const isHorizontal = effectiveViewMode === "horizontal-split";

  const showFilterToolbar = !!filters && !!setFilters;
  const showToolbarRow = hasTable || showFilterToolbar;

  return (
    <DreamerAttachmentModalShell
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title}
      {...headerSlots}
      onDownload={onDownload}
      isDownloading={isDownloading}
      canDownload={canDownload}
      generatedAt={generatedAt}
      contentPaddingClass="p-4"
    >
      <div className="flex flex-col gap-3 flex-1 min-h-0">
        {showToolbarRow && (
          <div className="flex justify-between items-center gap-2 flex-wrap shrink-0">
            {filters && setFilters ? (
              <DreamerAttachmentFilterToolbar
                filters={filters}
                setFilters={setFilters}
                filterOptions={filterOptions}
              />
            ) : (
              <div />
            )}
            {hasTable && (
              <ShowAsDropdown
                selectedMode={effectiveViewMode}
                options={VIEW_MODE_OPTIONS}
                onSelect={setViewMode}
                dataTest="chart-modal-show-as-trigger"
              />
            )}
          </div>
        )}
        <div
          className={cn(
            "flex flex-1 min-h-0 gap-2",
            isHorizontal ? "flex-row-reverse" : "flex-col"
          )}
        >
          {showChart && (
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto border border-border-border1 rounded-2xl py-6 px-4">
              <ChartBody
                chartKind={chartKind}
                height="100%"
                emptyVariant={emptyVariant}
                isPercentage={isPercentage}
                chartData={chartData}
                xAxisLabel={xAxisLabel}
                yAxisLabel={yAxisLabel}
                stackedChartData={stackedChartData}
                stackSegments={stackSegments}
                lineChartData={lineChartData}
                lineConfigs={lineConfigs}
                pieChartData={pieChartData}
                pieSize={220}
                gaugeValue={gaugeValue}
                gaugeTotal={gaugeTotal}
                gaugeCenterValue={gaugeCenterValue}
                gaugeCenterLabel={gaugeCenterLabel}
                gaugeTooltipLabel={gaugeTooltipLabel}
              />
            </div>
          )}
          {showTable && tableColumns && tableData && (
            <div className="flex-1 min-h-0 min-w-0 flex flex-col border border-border-border1 rounded-2xl overflow-hidden">
              <DataTable
                columns={tableColumns}
                data={tableData}
                firstColumnWidth={200}
                headerClassName="border-b border-t-0 border-border-border1 bg-surface-elevation1"
                className="w-full flex-1 min-h-0"
                isInfiniteScroll
                hasMore={tableHasMore}
                isLoadingMore={tableIsLoadingMore}
                onLoadMore={onTableLoadMore}
                isDataLoading={tableIsLoading}
                manualSorting
                initialSortBy={tableSorting}
                onSortingChange={onTableSortingChange}
                customEmptyState={
                  emptyVariant ? (
                    <DreamerAttachmentEmptyState variant={emptyVariant} />
                  ) : undefined
                }
              />
            </div>
          )}
        </div>
      </div>
    </DreamerAttachmentModalShell>
  );
};

export default ExpandedDreamerChartModal;
